import { Component, type ReactElement } from 'react';
import log from '@atlassian/jira-common-util-logging/src/log';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import type { BaseUrl, ProjectKey, IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	STATUS_FIELD_FETCH_VIEW_WORKFLOW,
	PERMISSION_VIEW_READONLY_WORKFLOW,
	PERMISSION_ADMINISTER,
} from '../../../common/constants';
import type { ViewWorkflowData, ViewWorkflowServiceGraphQL } from './types';
import { transform } from './utils';

export type ServiceApi = {
	data: ViewWorkflowData | undefined;
	loading: boolean;
	error: FetchError | undefined;
	fetch: () => Promise<void>;
};

export type State = {
	data: ViewWorkflowData | undefined;
	error: FetchError | undefined;
	loading: boolean;
};

export type Props = {
	issueKey: IssueKey;
	isSimplified?: boolean;
	baseUrl: BaseUrl;
	projectKey?: ProjectKey;
	children: (arg1: ServiceApi) => ReactElement;
};

export const graphQlUrl = (_baseUrl: BaseUrl): string => '/rest/gira/latest/';

const optionsGraphQlQuery = (issueKey: IssueKey, projectKey?: ProjectKey) => `
    query nextGenViewWorkflowQuery {
        permissions(issueKey:"${issueKey}", keys:["${PERMISSION_VIEW_READONLY_WORKFLOW}", "${PERMISSION_ADMINISTER}"]) {
            key
            havePermission
        }
        viewIssue(issueKey: "${issueKey}") {
            fields {
                ... on IssueTypeField {
                    fieldKey
                    id
                }
            }
        }
        ${
					projectKey
						? `project(projectKey: "${projectKey}") {
            projectId
            simplified
            type
        }`
						: ''
				}
    }
`;

export const getViewWorkflowService: ViewWorkflowServiceGraphQL = (
	baseUrl,
	issueKey,
	projectKey,
	isSimplified,
) =>
	fetchJson(graphQlUrl(baseUrl), {
		method: 'POST',
		body: JSON.stringify({ query: optionsGraphQlQuery(issueKey, projectKey) }),
	}).then((data) => transform(data, isSimplified));

// ff use-view-workflow-service-hook_8y6bk cleanup: remove ViewWorkflowService and everything in this directory, it is replaced by useViewWorkflowData
// eslint-disable-next-line jira/react/no-class-components
class ViewWorkflowService extends Component<Props, State> {
	constructor(props: Props) {
		super(props);

		this.state = {
			data: undefined,
			loading: false,
			error: undefined,
		};
	}

	fetch = async () => {
		const { baseUrl, projectKey, issueKey, isSimplified } = this.props;
		this.request();
		try {
			const data = await getViewWorkflowService(baseUrl, issueKey, projectKey, isSimplified);
			this.success(data);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (ex: any) {
			log.safeErrorWithoutCustomerData(STATUS_FIELD_FETCH_VIEW_WORKFLOW, ex);
			this.failure(ex);
		}
	};

	request = () => {
		this.setState({ error: undefined, loading: true, data: undefined });
	};

	success = (data?: ViewWorkflowData) => {
		this.setState({ loading: false, data });
	};

	failure = (error: FetchError) => {
		this.setState({ error, loading: false });
	};

	render() {
		return this.props.children({ ...this.state, fetch: this.fetch });
	}
}

export { ViewWorkflowService };
